import { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Badge,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
} from '@mui/material';
import { Search, Star } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { HCWCategory } from '@/types/hcw';
import {
  HEALTHWORKER_STARS,
  type HealthworkerIdentifier,
  type HealthworkerColor,
} from '@config/healthworker-stars';
import { getCategoryColor } from '@config/category-colors';
import { metadataStorage } from '@services/storage/metadata-storage';
import { logger } from '@utils/logger';
import { useLocalizedField } from '@utils/i18n-helpers';

interface HCWCategorySelectorProps {
  onSelect: (
    category: HCWCategory,
    starId: HealthworkerIdentifier,
    starColor: HealthworkerColor
  ) => void;
  recentCounts?: Record<string, number>;
}

export function HCWCategorySelector({
  onSelect,
  recentCounts = {},
}: HCWCategorySelectorProps) {
  const { t } = useTranslation();
  const getField = useLocalizedField();
  const [categories, setCategories] = useState<HCWCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    logger.info('HCWCategorySelector: Loading categories...');
    const allCategories = await metadataStorage.getAllHCWCategories();
    logger.info(`HCWCategorySelector: Loaded ${allCategories.length} categories`, allCategories);
    setCategories(allCategories);
  };

  const filteredCategories = categories
    .filter((cat) =>
      getField(cat, 'name').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const countA = recentCounts[a.code] || 0;
      const countB = recentCounts[b.code] || 0;
      if (countB !== countA) return countB - countA;
      return a.sortOrder - b.sortOrder;
    });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('hcwSelector.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('hcwSelector.subtitle')}
      </Typography>

      <TextField
        placeholder={t('hcwSelector.searchPlaceholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      <Grid container spacing={2}>
        {filteredCategories.map((category) => (
          <Grid item xs={6} sm={4} key={category.code}>
            <Badge
              badgeContent={recentCounts[category.code] || 0}
              color="primary"
              sx={{ width: '100%' }}
            >
              <Paper
                variant="outlined"
                sx={{
                  width: '100%',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  borderLeft: `4px solid ${getCategoryColor(category.parentCategory)}`,
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  {getField(category, 'name')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {HEALTHWORKER_STARS.map((star) => (
                    <IconButton
                      key={star.id}
                      aria-label={`${t(`stars.${star.colorName}`)} - ${getField(category, 'name')}`}
                      onClick={() => onSelect(category, star.id, star.colorName)}
                      sx={{
                        p: 0.5,
                        minWidth: 44,
                        minHeight: 44,
                        color: star.colorMuted,
                        '&:hover': {
                          color: star.color,
                        },
                        '&:active': {
                          color: star.color,
                        },
                      }}
                    >
                      <Star sx={{ fontSize: 28 }} />
                    </IconButton>
                  ))}
                </Box>
              </Paper>
            </Badge>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
